import { api } from "./api";

// Types pour l'API de recherche
export type SearchOperator =
  | "EQUAL"
  | "NOT_EQUAL"
  | "GREATER_THAN"
  | "LESS_THAN"
  | "GREATER_THAN_OR_NULL" 
  | "LESS_THAN_OR_NULL"
  | "IN"
  | "NOT_IN"
  | "LIKE"
  | "NOT_LIKE"
  | "BETWEEN"
  | "NOT_BETWEEN"
  | "IS_NULL"
  | "IS_NOT_NULL";

export type SearchType = "SINGLE" | "LIST" | "SEARCH" | "DATE" | "DATE_RANGE";

export type SortDirection = "ASC" | "DESC";

export interface SearchQuery {
  type: SearchType;
  operator: SearchOperator;
  field: string | string[];
  value?: any;
}

export interface SearchOptions {
  paginate?: {
    pageSize: number;
    pageNumber: number;
  };
  order?: {
    by: string;
    direction: SortDirection;
  };
  queries: SearchQuery[];
}

export interface SearchResponse<T> {
  page: number;
  size: number;
  totalPages: number;
  totalElements: number;
  data: T[];
}

/**
 * Effectue une recherche via l'API avec filtres avancés
 *
 * ⚠️ Par défaut, un échec est avalé et renvoie une page vide : l'appelant ne peut alors pas
 * distinguer « aucun résultat » d'« appel en erreur ». C'est acceptable pour les listes historiques,
 * qui affichent le même état vide dans les deux cas, mais pas pour un drill-down depuis un
 * indicateur qui vient d'annoncer un total non nul — le décalage donnerait l'impression que le
 * chiffre est faux. Passer `throwOnError` pour propager l'erreur et pouvoir l'afficher.
 *
 * @param entityType - Type d'entité à rechercher (ex: "parts", "workorders")
 * @param searchOptions - Options de recherche (filtres, pagination, tri)
 * @param all - Utilise l'endpoint `search-all` (sans pagination)
 * @param isAdmin - Cible l'espace `admin` plutôt que `users`
 * @param throwOnError - Rejette la promesse au lieu de renvoyer une page vide
 */
export const searchWithFilters = <T>(
  entityType: string,
  searchOptions: SearchOptions,
  all?: boolean,
  isAdmin?: boolean,
  throwOnError?: boolean
): Promise<SearchResponse<T>> => {
  return new Promise((resolve, reject) => {
    api
      .post(`${isAdmin ? "admin" : "users"}/${entityType}/search`+(all ? "-all" : ""), searchOptions)
      .then((response: any) => {
        resolve({
          page: response.page || 0,
          size: response.size || 10,
          totalPages: response.totalPages || 0,
          totalElements: response.totalElements || 0,
          data: response.data || [],
        });
      })
      .catch((error) => {
        console.error(`Erreur lors de la recherche de ${entityType}:`, error);

        if (throwOnError) {
          reject(error);
          return;
        }

        resolve({
          page: 0,
          size: 10,
          totalPages: 0,
          totalElements: 0,
          data: [],
        });
      });
  });
};

/**
 * toSearchOrder - traduit l'état de tri d'une table en clause `order` de l'API.
 *
 * Les vues de liste stockent leur tri sous la forme `{ head, orderDesc }` et doivent le transmettre
 * deux fois : à leur propre chargement et à l'export, qui doit rendre le même ordre que celui
 * affiché. Un seul endroit fait la conversion, plutôt que six copies du même ternaire.
 *
 * @param head - colonne triée
 * @param orderDesc - tri descendant
 */
export const toSearchOrder = (
  head: string,
  orderDesc: boolean
): { by: string; direction: SortDirection } => ({
  by: head,
  direction: orderDesc ? "DESC" : "ASC"
});

/**
 * Fonction utilitaire pour créer une requête de type "SEARCH" avec l'opérateur "LIKE"
 * @param field - Champ(s) sur lequel effectuer la recherche
 * @param value - Valeur à rechercher
 * @param operator - Opérateur de comparaison (LIKE, NOT_LIKE)
 */
export const createTextSearchQuery = (
  field: string | string[],
  value: string,
  operator: "LIKE" | "NOT_LIKE" = "LIKE"
): SearchQuery => {
  // Si field est une chaîne, la convertir en tableau
  const fieldArray = Array.isArray(field) ? field : [field];

  return {
    type: "SEARCH",
    operator,
    field: fieldArray,
    value,
  };
};

/**
 * Fonction utilitaire pour créer une requête de type "SINGLE" avec l'opérateur "EQUAL"
 * @param field - Champ sur lequel effectuer la recherche
 * @param value - Valeur à rechercher
 * @param operator - Opérateur de comparaison (EQUAL, NOT_EQUAL, GREATER_THAN, LESS_THAN, IS_NULL, IS_NOT_NULL)
 */
export const createSingleQuery = (
  field: string,
  value: any,
  operator: "EQUAL" | "NOT_EQUAL" | "GREATER_THAN" | "LESS_THAN" | "IS_NULL" | "IS_NOT_NULL" = "EQUAL"
): SearchQuery => {
  return {
    type: "SINGLE",
    operator,
    field,
    value,
  };
};

/**
 * Fonction utilitaire pour créer une requête de type "LIST"
 * @param field - Champ sur lequel effectuer la comparaison
 * @param values - Liste de valeurs à comparer
 * @param operator - Opérateur de comparaison (IN, NOT_IN)
 */
export const createListQuery = (
  field: string,
  values: (string | number | boolean)[],
  operator: "IN" | "NOT_IN" = "IN"
): SearchQuery => {
  return {
    type: "LIST",
    operator,
    field,
    value: values,
  };
};

/**
 * Fonction utilitaire pour créer une requête de type "DATE"
 * @param field - Champ date sur lequel effectuer la recherche
 * @param value - Valeur de date à comparer
 * @param operator - Opérateur de comparaison (EQUAL, NOT_EQUAL, GREATER_THAN, LESS_THAN)
 */
export const createDateQuery = (
  field: string,
  value: string | Date,
  operator: "EQUAL" | "NOT_EQUAL" | "GREATER_THAN" | "LESS_THAN" | "GREATER_THAN_OR_NULL" | "LESS_THAN_OR_NULL" = "EQUAL"
): SearchQuery => {
  const dateValue = value instanceof Date ? value.toISOString() : value;

  return {
    type: "DATE",
    operator,
    field,
    value: dateValue,
  };
};

/**
 * Fonction utilitaire pour créer une requête de type "DATE_RANGE"
 * @param field - Champ date sur lequel effectuer la recherche
 * @param startDate - Date de début
 * @param endDate - Date de fin
 * @param operator - Opérateur de comparaison (BETWEEN, NOT_BETWEEN)
 */
export const createDateRangeQuery = (
  field: string,
  startDate: string | Date,
  endDate: string | Date,
  operator: "BETWEEN" | "NOT_BETWEEN" = "BETWEEN"
): SearchQuery => {
  const start = startDate instanceof Date ? startDate.toISOString() : startDate;
  const end = endDate instanceof Date ? endDate.toISOString() : endDate;

  return {
    type: "DATE_RANGE",
    operator,
    field,
    value: [start, end],
  };
};
